/**
 * B87 leftovers (2026-09-08, critic 3 + 4) — the ONE rule for reading a week
 * number out of `/plan/week/:n`.
 *
 * Two readers used to have two rules. `routes/plan-week.tsx` ran
 * `Number.parseInt`, which takes `6abc`, `6.9` and `" 6"` and answers 6;
 * `App.tsx`'s document title ran `/^\/plan\/week\/(\d+)$/`, which refuses all
 * three. So `/v2/plan/week/6abc` put "Week 6" in the h1 and "Tayro" in the
 * tab, and `/v2/plan/week/99999999999999999999999` rendered a hero titled
 * "Week 1e+21" — a number the URL never carried, echoed back at the athlete.
 *
 * The rule: the raw param, exactly, is one to three digits, and the value is
 * at least 1. Not a prefix, not a float, not a sign, not exponential, no
 * surrounding space — `Number.parseInt` does all of those and is why this
 * module exists. Anything else is `null`, and both readers then say "Week"
 * and "Tayro" rather than printing a URL fragment.
 *
 * 999 is the ceiling because a season is a season: three digits is already
 * nineteen years of weeks, and past it the route's own "that isn't a week we
 * know about" surface is the honest answer.
 *
 * Pure, no React, no router — the title effect reads it off a pathname and
 * the route reads it off `useParams`, and they cannot disagree.
 */

/** One to three digits and nothing else. `\d` only: no `+`, `.`, `e` or space. */
const WEEK_PARAM = /^\d{1,3}$/;

/** The week number in `/plan/week/:n`, or null when the param is not one. */
export function parseWeekParam(raw: string | null | undefined): number | null {
  if (raw == null) return null;
  if (!WEEK_PARAM.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return n >= 1 ? n : null;
}
